import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// 예약중·미확인·출석·결석 예약의 출결 변경. 현재 상태와 같은 선택지는 막는다
const OPTIONS = [
  { action: 'ATTEND', label: '출석', Icon: CheckCircleOutlineIcon, color: '#1976D2', tint: '#EEF5FD', status: 'ATTENDED' },
  { action: 'ABSENT', label: '결석', Icon: HighlightOffIcon, color: '#F44336', tint: '#FEF0EF', status: 'ABSENT' },
  { action: 'CANCEL', label: '예약취소', Icon: EventBusyIcon, color: '#595959', tint: '#F5F5F5' },
];

// 목록 출결상태 배지와 같은 색
const STATUS_BADGE = {
  RESERVED: { label: '예약중', color: '#1565C0', background: '#E3F2FD' },
  UNCONFIRMED: { label: '미확인', color: '#E65100', background: '#FFF3E0' },
  ATTENDED: { label: '출석', color: '#0F7F85', background: '#E0F2F1' },
  ABSENT: { label: '결석', color: '#F44336', background: '#FFEBEE' },
};

const CANCEL_NOTICE = {
  PRIVATE_LESSON: '개인레슨은 예약을 취소하면 수업도 함께 삭제됩니다.',
  DUET_LESSON: '듀엣레슨은 예약을 취소하면 수업과 상대 회원 예약도 함께 삭제됩니다.',
};

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: '12px', py: '5px' }}>
      <Typography sx={{ width: 64, flexShrink: 0, fontSize: '13px', color: '#8C8C8C' }}>{label}</Typography>
      <Typography sx={{ fontSize: '13px', color: '#262626', fontWeight: 500, wordBreak: 'keep-all' }}>{value || '-'}</Typography>
    </Box>
  );
}

function ChangeAttendanceModal({ row, onClose, onConfirm, isPending }) {
  const [action, setAction] = useState(null);

  useEffect(() => {
    setAction(null);
  }, [row]);

  if (!row) return null;

  const badge = STATUS_BADGE[row.status];
  const notDeducted = row.status === 'RESERVED' || row.status === 'UNCONFIRMED';
  const cancelNotice = action === 'CANCEL' ? CANCEL_NOTICE[row.classType] : null;

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5, pb: 1 }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#262626' }}>출결 변경</Typography>
        <IconButton size="small" onClick={onClose} aria-label="닫기" sx={{ color: '#8C8C8C' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ px: 3, pt: 1 }}>
        {/* 회원·수업 요약 */}
        <Box sx={{ border: '1px solid #EDEDED', borderRadius: '12px', background: '#FAFAFA', p: '14px 16px', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', mb: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#262626' }}>{row.memberName || '-'}</Typography>
              <Typography sx={{ fontSize: '12px', color: '#8C8C8C' }}>{row.phoneNumber || ''}</Typography>
            </Box>
            {badge && (
              <Box
                component="span"
                sx={{
                  flexShrink: 0, px: '10px', py: '3px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                  color: badge.color, background: badge.background,
                }}>
                현재 {badge.label}
              </Box>
            )}
          </Box>
          <Box sx={{ borderTop: '1px dashed #E0E0E0', pt: 1 }}>
            <InfoRow label="수업" value={`${row.classDate} ${row.startTime}~${row.endTime}`} />
            <InfoRow label="수업유형" value={[row.classTypeLabel, row.className].filter(Boolean).join(' · ')} />
            <InfoRow label="강사" value={row.trainerName} />
            <InfoRow label="수강권" value={row.ticketName} />
          </Box>
        </Box>

        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#262626', mb: 1 }}>변경할 출결</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {OPTIONS.map(({ action: optionAction, label, Icon, color, tint, status }) => {
            const current = status === row.status;
            const selected = action === optionAction;
            return (
              <Button
                key={optionAction}
                disabled={current || isPending}
                onClick={() => setAction(optionAction)}
                sx={{
                  display: 'flex', flexDirection: 'column', gap: '4px', height: '76px', borderRadius: '12px',
                  fontSize: '14px', fontWeight: 600, textTransform: 'none',
                  color: selected ? color : '#595959',
                  border: `1.5px solid ${selected ? color : '#E5E5E5'}`,
                  background: selected ? tint : '#fff',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: color, color, background: tint },
                  '&.Mui-disabled': { color: '#BFBFBF', background: '#FAFAFA', borderColor: '#EFEFEF' },
                }}>
                <Icon sx={{ fontSize: '24px' }} />
                <span>{label}{current && ' (현재)'}</span>
              </Button>
            );
          })}
        </Box>
        {action === 'CANCEL' && (
          <Box
            sx={{
              display: 'flex', gap: '8px', alignItems: 'flex-start', mt: '12px', p: '10px 12px',
              borderRadius: '8px', background: '#FFF4F3', color: '#C62828',
            }}>
            <InfoOutlinedIcon sx={{ fontSize: '18px', mt: '1px' }} />
            <Typography sx={{ fontSize: '13px', lineHeight: 1.5 }}>
              {notDeducted ? '예약이 취소되고 횟수는 차감되지 않습니다.' : '예약을 취소하면 차감된 횟수가 돌아옵니다.'}
              {cancelNotice && ` ${cancelNotice}`}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: '8px' }}>
        <Button
          onClick={onClose}
          sx={{
            flex: 1, height: '42px', borderRadius: '8px', border: '1px solid #D9D9D9', color: '#595959', fontWeight: 600,
            '&:hover': { background: '#F5F5F5', borderColor: '#D9D9D9' },
          }}>
          닫기
        </Button>
        <Button
          disabled={!action || isPending}
          onClick={() => onConfirm(action)}
          sx={{
            flex: 1, height: '42px', borderRadius: '8px', background: '#262626', color: '#fff', fontWeight: 600,
            '&:hover': { background: '#000' },
            '&.Mui-disabled': { background: '#E5E5E5', color: '#fff' },
          }}>
          {isPending ? '변경 중...' : '변경'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangeAttendanceModal;
